import {Component, OnInit, ChangeDetectionStrategy, Input} from '@angular/core';
import {Subject} from 'rxjs/Subject';
import {get} from 'lodash';
import {takeUntil} from 'rxjs/operators';

import {DiagnosticOrderActionsService} from '@ispm/test/services/diagnostic-order-actions.service';
import {DiagnosticOrderService} from '@ispm/test/services/diagnostic-order.service';
import {FormHandlerService} from '@ispm/dynamic-form/services/form-handler.service';
import {IDiagnosticOrder} from '@ispm/test/interfaces/diagnostic-order.interface';
import {ManageTagsService} from '@ispm/shared/services/manage-tags.service';
import {NotificationMessageService} from '@ispm/core/components/customise-notification/notifications-message.service';
import {TestBarActionInterface} from '@ispm/test/components/test-bar/test-bar-action.interface';
import {modalResponse} from '@ispm/layout/constants/modal-responses.enum';
import {tooltipPositions} from '@ispm/shared/directives/tooltips/tooltip.directive';

@Component({
  selector: 'ispm-manage-tags',
  templateUrl: './manage-tags.component.html',
  styleUrls: ['./manage-tags.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ManageTagsComponent implements OnInit {
  @Input() configuration: TestBarActionInterface;
  @Input() cssClass: object | string;
  @Input() diagnosticOrder: IDiagnosticOrder;
  @Input() isDisabled: boolean = false;
  @Input() styleItem: object | string;
  @Input() tooltipPosition: string = tooltipPositions.BOTTOM;
  @Input() tooltipText: string = 'Manage tags';

  enable = true; // because in the story GEN-124662 - Config default ON

  private cancelButtonText = 'Cancel'
  private destroyer$: Subject<void> = new Subject<void>();
  private diagnosticOrderId: string;
  private existingTags = { };
  private formId = 'MANAGE-TAGS-POPUP';
  private submitButtonText = 'Manage tags'
  private titleText = 'Manage tags'

  constructor(
    private _diagnosticOrderActionsService: DiagnosticOrderActionsService,
    private _diagnosticOrderService: DiagnosticOrderService,
    private _formHandlerService: FormHandlerService,
    private _manageTagsService: ManageTagsService,
    private _notificationMessageService: NotificationMessageService,
  ) { }

  ngOnInit(): void {
    if (this.configuration && this.configuration.enable) {
      this.enable = this.configuration.enable;
      this.formId = get(this.configuration, 'payload.formId', this.formId);
      this._formHandlerService.getForm(this.formId);
      this.titleText = get(this.configuration, 'payload.titleText', this.titleText);
      this.submitButtonText = get(this.configuration, 'payload.submitButtonText', this.submitButtonText);
      this.cancelButtonText = get(this.configuration, 'payload.cancelButtonText', this.cancelButtonText);
    }

    if (this.configuration && this.configuration.enable === false) {
      this.enable = this.configuration.enable;
    }

    this.existingTags = { manualTags: this.diagnosticOrder.manualTags };
    this.diagnosticOrderId = this.diagnosticOrder.id;
  }

  ngOnDestroy() {
    this.destroyer$.next();
    this.destroyer$.complete();
  }

  manageTags(): void {
    if (this.enable === false) {
      return;
    }

    this.existingTags = { manualTags: this.diagnosticOrder.manualTags };

    this._formHandlerService.popupDynamicFormModal(
      this.titleText,
      this.formId,
      this.existingTags,
      this.submitButtonText,
      this.cancelButtonText
    )
      .pipe(
        takeUntil(this.destroyer$.asObservable())
      )
      .subscribe((data: any) => {
        if (data.status === modalResponse.approve) {
          this.saveData(data.payload.formData.manualTags);
        }
      })
  }

  private saveData(manualTags: any[] = []): void {
    const newDiagnosticOrderFieldData = { manualTags: manualTags, id: this.diagnosticOrderId };

    this._diagnosticOrderService.updateDiagnosticOrderFields(newDiagnosticOrderFieldData)
      .subscribe(
        () => {
          this.existingTags = { manualTags: manualTags };
          this._diagnosticOrderActionsService.updateDiagnosticOrderFields(newDiagnosticOrderFieldData);
          this._notificationMessageService.success('The tags were updated successfully')
          this._manageTagsService.tagsChanged(true);
        },
        () => {
          this._notificationMessageService.error('Cannot save changes');
        }
      )
  }
}
